// Génération PDF côté client pour les bons de livraison.
// Même mécanique que devis/print.ts : window.open() + document.write() + impression navigateur.

export interface BlPrintData {
  // BL core
  numeroBl: string;
  statut: string;
  lignes: any[];
  transporteur?: string | null;
  datePassageTransporteur?: string | null;
  numeroSuivi?: string | null;
  nbColis?: number | null;
  nbPalettes?: number | null;
  createdAt?: string | null;
  // Devis
  devisNumero?: string | null;
  devisAdresse?: string | null;
  devisContactNom?: string | null;
  devisDateLivraisonSouhaitee?: string | null;
  // Client / identité
  clientAffiche?: string | null;
  auteurNom?: string | null;
}

function dateFR(d: string | Date | null | undefined): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
}

function getCommandee(l: any): number {
  return l.quantiteCommandee ?? l.quantite ?? 0;
}
function getLivree(l: any): number {
  return l.quantiteLivree ?? 0;
}
function getReste(l: any): number {
  return l.resteALivrer ?? Math.max(0, getCommandee(l) - getLivree(l));
}

function rowCommandee(l: any, idx: number): string {
  const qteCmd = getCommandee(l);
  const thumbHtml = l.imageUrl
    ? `<img src="${l.imageUrl}" style="width:44px;height:44px;object-fit:cover;border-radius:5px;border:1px solid #e5e7eb;" />`
    : `<div style="width:44px;height:44px;background:#f3f4f6;border-radius:5px;border:1px solid #e5e7eb;display:flex;align-items:center;justify-content:center;font-size:18px;">📦</div>`;
  return `
    <tr style="${idx % 2 === 0 ? "" : "background:#f8fafc;"}">
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;">${thumbHtml}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;">
        <div style="font-weight:600;font-size:13px;">${l.designation || "—"}</div>
        ${l.sku ? `<div style="font-size:11px;color:#6b7280;font-family:monospace;">${l.sku}</div>` : ""}
      </td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:700;">${qteCmd}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:left;font-size:11px;color:#6b7280;font-family:monospace;">${l.sku || "—"}</td>
    </tr>
  `;
}

function rowLivree(l: any, idx: number): string {
  const qteCmd = getCommandee(l);
  const qteLiv = getLivree(l);
  const sn: string[] = Array.isArray(l.sn) ? l.sn.filter(Boolean) : [];
  const dateExp = l.dateExpedition ? dateFR(l.dateExpedition) : "—";
  const snHtml = sn.length > 0
    ? `<div style="margin-top:4px;display:flex;flex-wrap:wrap;gap:4px;">${sn.map(s => `<span style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:4px;padding:1px 6px;font-family:monospace;font-size:10px;color:#1d4ed8;">${s}</span>`).join("")}</div>`
    : "";
  return `
    <tr style="${idx % 2 === 0 ? "" : "background:#f0fdf4;"}">
      <td style="padding:8px;border-bottom:1px solid #d1fae5;">
        <div style="font-weight:600;font-size:13px;">${l.designation || "—"}</div>
        ${l.sku ? `<div style="font-size:11px;color:#6b7280;font-family:monospace;">${l.sku}</div>` : ""}
        ${snHtml}
      </td>
      <td style="padding:8px;border-bottom:1px solid #d1fae5;text-align:center;font-weight:700;color:#16a34a;">${qteLiv} / ${qteCmd}</td>
      <td style="padding:8px;border-bottom:1px solid #d1fae5;text-align:center;font-size:11px;color:#6b7280;">${dateExp}</td>
    </tr>
  `;
}

function rowReste(l: any, idx: number): string {
  const reste = getReste(l);
  return `
    <tr style="${idx % 2 === 0 ? "background:#fff7ed;" : "background:#ffedd5;"}">
      <td style="padding:8px;border-bottom:1px solid #fed7aa;">
        <div style="font-weight:600;font-size:13px;">${l.designation || "—"}</div>
        ${l.sku ? `<div style="font-size:11px;color:#92400e;font-family:monospace;">${l.sku}</div>` : ""}
      </td>
      <td style="padding:8px;border-bottom:1px solid #fed7aa;text-align:center;font-weight:700;color:#c2410c;">${reste}</td>
    </tr>
  `;
}

function tableSection(title: string, colorBar: string, headerBg: string, rows: string[], headers: string[]): string {
  if (rows.length === 0) return "";
  return `
    <div style="margin-bottom:24px;">
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
        <div style="background:${colorBar};color:white;padding:10px 16px;font-size:12px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;">${title}</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:${headerBg};">
              ${headers.map(h => `<th style="padding:8px;text-align:left;font-size:11px;color:#64748b;font-weight:600;">${h}</th>`).join("")}
            </tr>
          </thead>
          <tbody>${rows.join("")}</tbody>
        </table>
      </div>
    </div>
  `;
}

export function printBonLivraison(bl: BlPrintData, logoUrl?: string) {
  const lignes: any[] = Array.isArray(bl.lignes) ? bl.lignes : [];

  const lignesCommandees = lignes;
  const lignesLivrees = lignes.filter(l => getLivree(l) > 0);
  const lignesAvecReste = lignes.filter(l => getReste(l) > 0);

  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="Jobotto" style="height:44px;max-width:150px;object-fit:contain;" />`
    : `<div style="font-size:26px;font-weight:800;color:#1e293b;letter-spacing:-0.5px;">JOBOTECH</div>`;

  const dateCreationStr = dateFR(bl.createdAt);
  const dateLivraisonStr = bl.devisDateLivraisonSouhaitee
    ? dateFR(bl.devisDateLivraisonSouhaitee)
    : bl.datePassageTransporteur
      ? dateFR(bl.datePassageTransporteur)
      : "—";

  const infoItems = ([
    ["N° Devis", bl.devisNumero ? `<strong style="color:#2563eb;font-size:14px;">${bl.devisNumero}</strong>` : "—"],
    ["Client", bl.clientAffiche || "—"],
    ["Contact", bl.devisContactNom || "—"],
    ["Date de création", dateCreationStr],
    ["Auteur", bl.auteurNom || "—"],
    ["Adresse de livraison", bl.devisAdresse ? bl.devisAdresse.replace(/\n/g, "<br>") : "—"],
    ["Date livraison estimée", dateLivraisonStr],
  ] as [string, string][]).filter(([, v]) => v !== "—");

  const infoGrid = infoItems.map(([label, value]) => `
    <div style="padding:6px 0;border-bottom:1px solid #f1f5f9;display:flex;gap:8px;">
      <span style="font-size:11px;color:#6b7280;font-weight:600;min-width:160px;flex-shrink:0;">${label}</span>
      <span style="font-size:12px;color:#1e293b;">${value}</span>
    </div>
  `).join("");

  // Transport block
  const hasTransport = bl.transporteur || bl.datePassageTransporteur || bl.numeroSuivi || bl.nbColis || bl.nbPalettes;
  const transportBlock = hasTransport ? `
    <div style="margin-bottom:24px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
      <div style="background:#0f172a;color:#fff;padding:10px 16px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Transport & Expédition</div>
      <div style="padding:14px;display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        ${bl.transporteur ? `<div><span style="font-size:10px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:2px;">Transporteur</span><span style="font-size:13px;font-weight:600;">${bl.transporteur}</span></div>` : ""}
        ${bl.datePassageTransporteur ? `<div><span style="font-size:10px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:2px;">Date de passage</span><span style="font-size:13px;font-weight:600;">${dateFR(bl.datePassageTransporteur)}</span></div>` : ""}
        ${bl.nbColis != null ? `<div><span style="font-size:10px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:2px;">Nombre de colis</span><span style="font-size:13px;font-weight:600;">${bl.nbColis}</span></div>` : ""}
        ${bl.nbPalettes != null ? `<div><span style="font-size:10px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:2px;">Nombre de palettes</span><span style="font-size:13px;font-weight:600;">${bl.nbPalettes}</span></div>` : ""}
        ${bl.numeroSuivi ? `<div style="grid-column:1/-1;"><span style="font-size:10px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;display:block;margin-bottom:2px;">Numéro de suivi</span><span style="font-size:13px;font-weight:600;font-family:monospace;color:#2563eb;">${bl.numeroSuivi}</span></div>` : ""}
      </div>
    </div>
  ` : "";

  const sectionCommandee = tableSection(
    "Articles commandés",
    "#1e293b",
    "#f1f5f9",
    lignesCommandees.map((l, i) => rowCommandee(l, i)),
    ["Photo", "Désignation", "Qté commandée", "SKU"]
  );

  const sectionLivree = tableSection(
    "Articles livrés",
    "#16a34a",
    "#f0fdf4",
    lignesLivrees.map((l, i) => rowLivree(l, i)),
    ["Désignation / N° de série", "Qté livrée / commandée", "Date expédition"]
  );

  const sectionReste = lignesAvecReste.length > 0 ? `
    <div style="margin-bottom:24px;">
      <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;overflow:hidden;">
        <div style="background:#ea580c;color:white;padding:10px 16px;font-size:12px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;">⚠ Reste à livrer</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#fff7ed;">
              <th style="padding:8px;text-align:left;font-size:11px;color:#92400e;font-weight:600;">Désignation</th>
              <th style="padding:8px;text-align:center;font-size:11px;color:#92400e;font-weight:600;">Quantité restante</th>
            </tr>
          </thead>
          <tbody>${lignesAvecReste.map((l, i) => rowReste(l, i)).join("")}</tbody>
        </table>
      </div>
    </div>
  ` : "";

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>BL ${bl.numeroBl}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e293b; background: white; font-size: 13px; }
    .page { max-width: 820px; margin: 0 auto; padding: 40px; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; }
      .no-print { display: none !important; }
      @page { margin: 0; }
      .page { padding: 15mm; max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="page">

    <!-- Bouton impression -->
    <div class="no-print" style="margin-bottom:20px;text-align:right;">
      <button onclick="window.print()" style="background:#1e293b;color:white;border:none;padding:10px 20px;border-radius:8px;font-size:14px;cursor:pointer;font-weight:600;">⬇ Enregistrer / Imprimer</button>
    </div>

    <!-- En-tête -->
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:20px;border-bottom:3px solid #1e293b;">
      <div>
        ${logoHtml}
        <div style="color:#64748b;font-size:11px;margin-top:4px;">contact@jobotto.fr</div>
        <div style="margin-top:6px;display:inline-block;background:#f0f4ff;border:1px solid #c7d2fe;border-radius:4px;padding:2px 8px;">
          <span style="font-size:10px;font-weight:700;color:#3730a3;letter-spacing:0.04em;">DISTRIBUTEUR OFFICIEL FRANCE — PUDU ROBOTICS</span>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:26px;font-weight:800;color:#1e293b;letter-spacing:-0.5px;">BON DE LIVRAISON</div>
        <div style="background:#1e293b;color:white;display:inline-block;padding:4px 16px;border-radius:4px;margin-top:6px;">
          <span style="font-size:18px;font-weight:800;letter-spacing:0.5px;">${bl.numeroBl}</span>
        </div>
      </div>
    </div>

    <!-- Bloc infos BL -->
    <div style="margin-bottom:24px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;">
      ${infoGrid}
    </div>

    <!-- Tableau Commandé -->
    ${sectionCommandee}

    <!-- Tableau Livré -->
    ${sectionLivree}

    <!-- Tableau Reste à livrer -->
    ${sectionReste}

    <!-- Bloc Transport -->
    ${transportBlock}

    <!-- Pied de page -->
    <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;text-align:center;">
      <div style="font-size:10px;color:#94a3b8;line-height:1.6;">
        Jobotech SAS · Distributeur officiel Pudu Robotics en France · contact@jobotto.fr<br>
        Document généré le ${new Date().toLocaleDateString("fr-FR")} · Ce document ne constitue pas une facture.
      </div>
    </div>

  </div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
